/* eslint-disable consistent-return */
import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SendbirdChat from '@sendbird/chat'
import log from 'sentry/log'
import {
  errorHandler as chatErrorHandler,
  fetchConnectProperties,
  setSendbirdUser,
  setSendbirdUserDisconnect,
  removeConnectProperties,
} from 'reduxBits/chat/actions'
import useDetectBrowserTabFocus from '../useDetectBrowserTabFocus'

export const useConnectStatus = () => {
  const sb = SendbirdChat.instance
  const [connected, setConnected] = useState(false)
  const { isBrowsingFiles, userSuspendedType } = useSelector((state) => state.chat)
  const dispatch = useDispatch()
  const focused = useDetectBrowserTabFocus()

  const connectToSb = useCallback(
    async (retries = 0) => {
      const maxRetries = 3
      try {
        const { sbUserId, sbSessionToken } = (await dispatch(fetchConnectProperties())) || {}
        if (!sbUserId || !sbSessionToken) return

        const sendbirdUser = await sb.connect(sbUserId, sbSessionToken)
        dispatch(setSendbirdUser(sendbirdUser))
        setConnected(true)
      } catch (error) {
        if (retries < maxRetries) {
          // retry to connect
          return connectToSb(retries + 1)
        }
        log.message(`connectToSb failed. Reason: ${error}`)
        return dispatch(
          chatErrorHandler({
            error,
            errorType: 'general',
            sentryLog: { description: 'Chat: Sendbird connection error' },
          }),
        )
      }
    },
    [dispatch, sb],
  )

  const disconnectSb = useCallback(async () => {
    try {
      await sb.disconnect()
      dispatch(setSendbirdUserDisconnect())
      setConnected(false)
    } catch (error) {
      log.message(`disconnectToSb failed. Reason: ${error}`)
      setConnected(false)
      return dispatch(
        chatErrorHandler({
          error,
          errorType: 'general',
          sentryLog: { description: 'Chat: Sendbird disconnection error' },
        }),
      )
    }
  }, [dispatch, sb])

  useEffect(() => {
    if (sb) {
      if (!connected && focused) {
        connectToSb()
      }

      if (connected && !focused && !isBrowsingFiles) {
        disconnectSb()
      }

      if (connected && userSuspendedType) {
        dispatch(removeConnectProperties())
        disconnectSb()
      }
    }
  }, [
    sb,
    connected,
    connectToSb,
    disconnectSb,
    isBrowsingFiles,
    focused,
    userSuspendedType,
    dispatch,
  ])

  return connected
}
